/**
 * Where the shared ranking lives, and who is asking for it.
 *
 * On kalampress and on the tool's own site this is same-origin and empty. In an
 * embed it is not: the script runs on somebody else's page, so `/api/picks`
 * would resolve against *their* origin and hit a 404 on a site that has never
 * heard of us. Every request the package makes goes through `endpoint()`.
 *
 * Kept as module state rather than passed through every call because the answer
 * is fixed for the lifetime of a page — the embed sets it once, before anything
 * else runs — and threading an options object through a keystroke path would be
 * noise at every layer.
 */

interface Config {
  /** Origin to reach the API at. Empty means same-origin. */
  origin: string;
  /** Identifies the integration to the API. Absent outside embeds. */
  apiKey: string | null;
}

const config: Config = { origin: '', apiKey: null };

export function configure(next: Partial<Config>): void {
  if (next.origin !== undefined) config.origin = next.origin.replace(/\/+$/, '');
  if (next.apiKey !== undefined) config.apiKey = next.apiKey;
}

export function endpoint(path: string): string {
  return `${config.origin}${path}`;
}

export function apiKey(): string | null {
  return config.apiKey;
}

/** Headers every call adds: the key, when the page is an embed with one. */
export function apiHeaders(base: Record<string, string> = {}): Record<string, string> {
  return config.apiKey ? { ...base, 'X-Api-Key': config.apiKey } : base;
}
